<template>
  <div>
    <!-- Header Section -->
    <div class="d-flex justify-content-between align-items-center mb-4">
      <div>
        <h1 class="h3 mb-1">Package Buffet Report</h1>
        <p class="text-muted mb-0">
          <i class="cil-calendar"></i>
          {{ currentDate }}
        </p>
      </div>
      <div class="d-flex gap-2">
        <button class="btn btn-outline-primary" @click="refreshData">
          <i class="cil-reload"></i> Refresh
        </button>
        <button class="btn btn-primary" @click="exportReport">
          <i class="cil-cloud-download"></i> Export
        </button>
      </div>
    </div>

    <!-- Statistics Cards -->
    <div class="row ">
      <div class="col-sm-6 col-lg-3" v-for="(stat, index) in statistics" :key="index">
        <div class="card mb-4 text-white" :class="stat.cardClass">
          <div class="card-body pb-0 d-flex justify-content-between align-items-start">
            <div>
              <div class="fs-4 fw-semibold">{{ stat.value }}</div>
              <div>{{ stat.label }}</div>
            </div>
            <div class="dropdown">
              <i :class="stat.icon" class="fs-1"></i>
            </div>
          </div>
          <div class="c-chart-wrapper mt-3 mx-3" style="height:70px;">
            <div class="progress progress-thin mt-3">
              <div class="progress-bar bg-white" :style="{ width: stat.progress + '%' }" role="progressbar">
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Filters Section -->
    <div class="card ">
      <div class="card-header">
        <h5 class="mb-0">Filters</h5>
      </div>
      <div class="card-body">
        <div class="row ">
          <div class="col-md-3">
            <label class="form-label">Date Range</label>
            <select class="form-select" v-model="selectedDateRange" @change="applyFilters">
              <option value="7">Last 7 days</option>
              <option value="30" selected>Last 30 days</option>
              <option value="90">Last 90 days</option>
              <option value="365">Last year</option>
            </select>
          </div>
          <div class="col-md-3">
            <label class="form-label">Package Type</label>
            <select class="form-select" v-model="selectedPackageType" @change="applyFilters">
              <option value="">All Types</option>
              <option value="basic">Basic</option>
              <option value="premium">Premium</option>
              <option value="deluxe">Deluxe</option>
              <option value="enterprise">Enterprise</option>
            </select>
          </div>
          <div class="col-md-3">
            <label class="form-label">Status</label>
            <select class="form-select" v-model="selectedStatus" @change="applyFilters">
              <option value="">All Status</option>
              <option value="active">Active</option>
              <option value="pending">Pending</option>
              <option value="expired">Expired</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
          <div class="col-md-3">
            <label class="form-label">Search</label>
            <div class="input-group">
              <input type="text" class="form-control" placeholder="Search packages..." v-model="searchQuery"
                @input="applyFilters">
              <button class="btn btn-outline-primary" type="button">
                <i class="cil-magnifying-glass"></i>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Charts Section -->
    <div class="row ">
      <div class="col-lg-8">
        <div class="card">
          <div class="card-header">
            <h5 class="card-title mb-0">Package Sales Trend</h5>
            <p class="text-muted small mb-0">Monthly revenue and package count</p>
          </div>
          <div class="card-body">
            <div class="chart-container" style="height: 300px;">
              <CChartLine :datasets="salesChartData" :options="salesChartOptions" style="height: 300px;" />
            </div>
          </div>
        </div>
      </div>
      <div class="col-lg-4">
        <div class="card">
          <div class="card-header">
            <h5 class="card-title mb-0">Package Distribution</h5>
            <p class="text-muted small mb-0">By package type</p>
          </div>
          <div class="card-body">
            <div class="chart-container" style="height: 300px;">
              <CChartDoughnut :data="distributionChartData" :options="distributionChartOptions"
                style="height: 300px;" />
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Package Table -->
    <div class="card">
      <div class="card-header">
        <div class="d-flex justify-content-between align-items-center">
          <div>
            <h5 class="card-title mb-0">Package Details</h5>
            <p class="text-muted small mb-0">{{ filteredPackages.length }} packages found</p>
          </div>
          <div class="d-flex gap-2">
            <button class="btn btn-sm btn-outline-primary" @click="sortBy('name')">
              <i class="cil-sort-alpha-down"></i> Sort A-Z
            </button>
            <button class="btn btn-sm btn-outline-primary" @click="sortBy('price')">
              <i class="cil-sort-numeric-down"></i> Sort Price
            </button>
          </div>
        </div>
      </div>
      <div class="card-body p-0">
        <div class="table-responsive">
          <table class="table table-hover mb-0">
            <thead class="table-light">
              <tr>
                <th>Package Name</th>
                <th>Type</th>
                <th>Price</th>
                <th>Sales</th>
                <th>Revenue</th>
                <th>Status</th>
                <th>Performance</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="pkg in paginatedPackages" :key="pkg.id">
                <td>
                  <div class="d-flex align-items-center">
                    <div class="avatar me-3">
                      <div class="avatar-initial bg-primary rounded">
                        <i class="cil-puzzle"></i>
                      </div>
                    </div>
                    <div>
                      <div class="fw-semibold">{{ pkg.name }}</div>
                      <div class="text-muted small">{{ pkg.description }}</div>
                    </div>
                  </div>
                </td>
                <td>
                  <span class="badge" :class="getTypeClass(pkg.type)">
                    {{ pkg.type }}
                  </span>
                </td>
                <td class="fw-semibold">${{ pkg.price.toLocaleString() }}</td>
                <td>{{ pkg.sales.toLocaleString() }}</td>
                <td class="fw-semibold text-success">${{ pkg.revenue.toLocaleString() }}</td>
                <td>
                  <span class="badge" :class="getStatusClass(pkg.status)">
                    {{ pkg.status }}
                  </span>
                </td>
                <td>
                  <div class="progress progress-thin">
                    <div class="progress-bar" :class="getPerformanceClass(pkg.performance)"
                      :style="{ width: pkg.performance + '%' }" role="progressbar">
                    </div>
                  </div>
                  <small class="text-muted">{{ pkg.performance }}%</small>
                </td>
                <td>
                  <div class="dropdown">
                    <button class="btn btn-ghost-primary btn-sm dropdown-toggle" type="button"
                      :id="'dropdown-' + pkg.id" data-bs-toggle="dropdown">
                      <i class="cil-options"></i>
                    </button>
                    <ul class="dropdown-menu">
                      <li><a class="dropdown-item" href="#" @click="viewDetails(pkg)">
                          <i class="cil-eye"></i> View Details
                        </a></li>
                      <li><a class="dropdown-item" href="#" @click="editPackage(pkg)">
                          <i class="cil-pencil"></i> Edit
                        </a></li>
                      <li>
                        <hr class="dropdown-divider">
                      </li>
                      <li><a class="dropdown-item text-danger" href="#" @click="deletePackage(pkg)">
                          <i class="cil-trash"></i> Delete
                        </a></li>
                    </ul>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Pagination -->
      <div class="card-footer" v-if="totalPages > 1">
        <nav>
          <ul class="pagination justify-content-center mb-0">
            <li class="page-item" :class="{ disabled: currentPage === 1 }">
              <a class="page-link" href="#" @click.prevent="changePage(currentPage - 1)">
                <i class="cil-chevron-left"></i>
              </a>
            </li>
            <li v-for="page in visiblePages" :key="page" class="page-item" :class="{ active: page === currentPage }">
              <a class="page-link" href="#" @click.prevent="changePage(page)">{{ page }}</a>
            </li>
            <li class="page-item" :class="{ disabled: currentPage === totalPages }">
              <a class="page-link" href="#" @click.prevent="changePage(currentPage + 1)">
                <i class="cil-chevron-right"></i>
              </a>
            </li>
          </ul>
        </nav>
      </div>
    </div>
  </div>
</template>

<script>
import { CChartLine, CChartDoughnut, CChartBar, CChartPolarArea } from "@coreui/vue-chartjs";
export default {
  name: 'PackageReport',
  data() {
    return {
      currentDate: new Date().toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      }),
      searchQuery: '',
      selectedDateRange: '30',
      selectedPackageType: '',
      selectedStatus: '',
      currentPage: 1,
      itemsPerPage: 10,
      sortField: '',
      sortOrder: 'asc',

      statistics: [
        { label: 'Total Packages', value: '2,549', cardClass: 'bg-primary', icon: 'cil-puzzle', progress: 85 },
        { label: 'Active Sales', value: '1,847', cardClass: 'bg-success', icon: 'cil-chart-line', progress: 72 },
        { label: 'Total Revenue', value: '$487.2K', cardClass: 'bg-warning', icon: 'cil-dollar', progress: 91 },
        { label: 'Customer Rating', value: '4.8/5', cardClass: 'bg-info', icon: 'cil-star', progress: 96 }
      ],

      packages: [
        { id: 1, name: 'Premium Buffet Package', description: 'All-inclusive premium dining experience', type: 'premium', price: 299, sales: 1250, revenue: 373750, status: 'active', performance: 89 },
        { id: 2, name: 'Basic Family Package', description: 'Perfect for family gatherings', type: 'basic', price: 149, sales: 2100, revenue: 312900, status: 'active', performance: 76 },
        { id: 3, name: 'Deluxe Corporate Package', description: 'Ideal for business events', type: 'deluxe', price: 499, sales: 890, revenue: 444110, status: 'active', performance: 92 },
        { id: 4, name: 'Enterprise Catering', description: 'Large-scale event catering', type: 'enterprise', price: 799, sales: 420, revenue: 335580, status: 'pending', performance: 68 },
        { id: 5, name: 'Weekend Special', description: 'Limited weekend offer', type: 'premium', price: 199, sales: 780, revenue: 155220, status: 'expired', performance: 45 },
        { id: 6, name: 'Holiday Feast Package', description: 'Special holiday menu', type: 'deluxe', price: 399, sales: 650, revenue: 259350, status: 'active', performance: 83 }
      ],

      filteredPackages: [],
      salesChartData: {
        labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'],
        datasets: [
          {
            label: 'Revenue',
            backgroundColor: 'rgba(0, 123, 255, 0.1)',
            borderColor: '#007bff',
            pointBackgroundColor: '#007bff',
            data: [65000, 78000, 85000, 92000, 87000, 95000],
            tension: 0.4,
            fill: true,
          },
        ],
      },
      salesChartOptions: {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          y: {
            beginAtZero: true,
            ticks: {
              callback: function (value) {
                return '$' + value.toLocaleString();
              },
            },
          },
        },
      },

      distributionChartData: {
        labels: ['Basic', 'Premium', 'Deluxe', 'Enterprise'],
        datasets: [
          {
            backgroundColor: ['#6c757d', '#007bff', '#ffc107', '#28a745'],
            data: [35, 30, 20, 15],
          },
        ],
      },
      distributionChartOptions: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            position: 'bottom',
          },
        },
      },
    };
  },
  components: {
    CChartLine,
    CChartDoughnut,
    CChartBar,
    CChartPolarArea,
  },
  computed: {
    paginatedPackages() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      return this.filteredPackages.slice(start, start + this.itemsPerPage);
    },
    totalPages() {
      return Math.ceil(this.filteredPackages.length / this.itemsPerPage);
    },
    visiblePages() {
      const total = this.totalPages;
      const current = this.currentPage;
      const pages = [];
      if (total <= 7) {
        for (let i = 1; i <= total; i++) pages.push(i);
      } else if (current <= 4) {
        for (let i = 1; i <= 5; i++) pages.push(i);
        pages.push('...', total);
      } else if (current >= total - 3) {
        pages.push(1, '...');
        for (let i = total - 4; i <= total; i++) pages.push(i);
      } else {
        pages.push(1, '...');
        for (let i = current - 1; i <= current + 1; i++) pages.push(i);
        pages.push('...', total);
      }
      return pages;
    }
  },
  methods: {
    refreshData() {
      console.log('Refreshing data...');
      this.applyFilters();
    },
    exportReport() {
      const csvContent = this.generateCSV();
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'package-buffet-report.csv';
      link.click();
      window.URL.revokeObjectURL(url);
    },
    generateCSV() {
      const headers = ['Package Name', 'Type', 'Price', 'Sales', 'Revenue', 'Status', 'Performance'];
      const rows = this.filteredPackages.map(pkg => [
        pkg.name, pkg.type, pkg.price, pkg.sales, pkg.revenue, pkg.status, pkg.performance + '%'
      ]);
      return [headers, ...rows].map(row => row.join(',')).join('\n');
    },
    applyFilters() {
      let filtered = [...this.packages];
      if (this.searchQuery) {
        const q = this.searchQuery.toLowerCase();
        filtered = filtered.filter(pkg =>
          pkg.name.toLowerCase().includes(q) || pkg.description.toLowerCase().includes(q));
      }
      if (this.selectedPackageType) {
        filtered = filtered.filter(pkg => pkg.type === this.selectedPackageType);
      }
      if (this.selectedStatus) {
        filtered = filtered.filter(pkg => pkg.status === this.selectedStatus);
      }
      this.filteredPackages = filtered;
      this.currentPage = 1;
    },
    sortBy(field) {
      if (this.sortField === field) {
        this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc';
      } else {
        this.sortField = field;
        this.sortOrder = 'asc';
      }
      this.filteredPackages.sort((a, b) => {
        let aVal = a[field], bVal = b[field];
        if (typeof aVal === 'string') {
          aVal = aVal.toLowerCase(); bVal = bVal.toLowerCase();
        }
        return this.sortOrder === 'asc' ? (aVal > bVal ? 1 : -1) : (aVal < bVal ? 1 : -1);
      });
    },
    changePage(page) {
      if (page >= 1 && page <= this.totalPages) this.currentPage = page;
    },
    getTypeClass(type) {
      const classes = { basic: 'bg-secondary', premium: 'bg-primary', deluxe: 'bg-warning', enterprise: 'bg-success' };
      return classes[type] || 'bg-secondary';
    },
    getStatusClass(status) {
      const classes = { active: 'bg-success', pending: 'bg-warning', expired: 'bg-danger', cancelled: 'bg-secondary' };
      return classes[status] || 'bg-secondary';
    },
    getPerformanceClass(perf) {
      return perf >= 80 ? 'bg-success' : perf >= 60 ? 'bg-warning' : 'bg-danger';
    },
    viewDetails(pkg) { console.log('Viewing details for:', pkg.name); },
    editPackage(pkg) { console.log('Editing package:', pkg.name); },
    deletePackage(pkg) {
      if (confirm(`Are you sure you want to delete ${pkg.name}?`)) {
        const index = this.packages.findIndex(p => p.id === pkg.id);
        if (index > -1) {
          this.packages.splice(index, 1);
          this.applyFilters();
        }
      }
    }
  },
  mounted() {
    this.filteredPackages = [...this.packages];
  }
};
</script>
